import type { TimerDuration, TimerMode, TimerStatus } from './types';

/** 定时器运行时会话（按 sessionId 缓存，跨组件 remount 保留） */
export type TimerSessionState = {
  target: TimerDuration;
  mode: TimerMode;
  elapsedMs: number;
  status: TimerStatus;
  isSettingMode: boolean;
  elapsedBase: number;
  runningSince: number | null;
};

export type TimerCommand = 'togglePlayPause' | 'reset';

const sessions = new Map<string, TimerSessionState>();
const sessionVersions = new Map<string, number>();
const sessionListeners = new Map<string, Set<() => void>>();
const commandListeners = new Map<string, Set<(command: TimerCommand) => void>>();

function notifySession(sessionId: string) {
  sessionVersions.set(sessionId, (sessionVersions.get(sessionId) ?? 0) + 1);
  sessionListeners.get(sessionId)?.forEach((listener) => listener());
}

/** 读取会话（同一写入周期内返回稳定引用，供 useSyncExternalStore 安全使用） */
export function readTimerSession(sessionId: string): TimerSessionState | undefined {
  return sessions.get(sessionId);
}

/** useSyncExternalStore 用版本号订阅，避免 getSnapshot 每次返回新对象 */
export function getTimerSessionVersion(sessionId: string): number {
  return sessionVersions.get(sessionId) ?? 0;
}

/** remount 时恢复运行中会话的 elapsed，写回 store 但不通知订阅者 */
export function hydrateTimerSessionForMount(sessionId: string): TimerSessionState | undefined {
  const saved = sessions.get(sessionId);
  if (!saved) return undefined;
  if (saved.status !== 'running' || saved.runningSince == null) return saved;

  const elapsedMs = saved.elapsedBase + (performance.now() - saved.runningSince);
  const hydrated: TimerSessionState = {
    ...saved,
    elapsedMs,
    elapsedBase: elapsedMs,
    runningSince: performance.now(),
  };
  sessions.set(sessionId, hydrated);
  return hydrated;
}

export function writeTimerSession(sessionId: string, state: TimerSessionState): void {
  sessions.set(sessionId, state);
  notifySession(sessionId);
}

export function clearTimerSession(sessionId: string): void {
  sessions.delete(sessionId);
  sessionVersions.delete(sessionId);
  notifySession(sessionId);
}

export function subscribeTimerSession(sessionId: string, listener: () => void): () => void {
  let set = sessionListeners.get(sessionId);
  if (!set) {
    set = new Set();
    sessionListeners.set(sessionId, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set?.size === 0) sessionListeners.delete(sessionId);
  };
}

export function subscribeTimerCommands(
  sessionId: string,
  listener: (command: TimerCommand) => void,
): () => void {
  let set = commandListeners.get(sessionId);
  if (!set) {
    set = new Set();
    commandListeners.set(sessionId, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set?.size === 0) commandListeners.delete(sessionId);
  };
}

export function dispatchTimerCommand(sessionId: string, command: TimerCommand): void {
  commandListeners.get(sessionId)?.forEach((listener) => listener(command));
}
